import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import CartSidebar from './CartSidebar';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isExperienceOpen, setIsExperienceOpen] = useState(false);
  const experienceRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { items } = useCart();

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (experienceRef.current && !experienceRef.current.contains(event.target as Node)) {
        setIsExperienceOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { name: 'ABOUT', path: '/about' },
    { name: 'THE SPACE', path: '/the-space' },
  ];

  const experienceLinks = [
    { name: 'REFORMER PILATES', path: '/reformer-pilates' },
    { name: 'THERMAL SUITES', path: '/thermal-suites' },
    { name: 'CAFÉ', path: '/cafe' },
  ];

  const rightNavLinks = [
    { name: 'PRICING', path: '/pricing' },
    { name: 'LOCATION', path: '/location' },
    { name: 'CONTACT', path: '/contact' },
    { name: 'SHOP', path: '/shop' },
  ];

  const isExperiencePage = experienceLinks.some(link => location.pathname === link.path);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="px-6 md:px-12 py-6">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center cursor-pointer">
              <img
                src="https://static.readdy.ai/image/c547be34c1bf7bb39243860cf03ecf7c/b9a533f96a7d792ab1739b73d5695be7.png"
                alt="RE:SET"
                className="h-8 md:h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-10">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-xs font-semibold tracking-widest transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                    location.pathname === link.path
                      ? 'text-reset-sage'
                      : 'text-reset-dark hover:text-reset-sage'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Experience Dropdown */}
              <div ref={experienceRef} className="relative">
                <button
                  onClick={() => setIsExperienceOpen(!isExperienceOpen)}
                  className={`flex items-center gap-1 text-xs font-semibold tracking-widest transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                    isExperiencePage
                      ? 'text-reset-sage'
                      : 'text-reset-dark hover:text-reset-sage'
                  }`}
                >
                  EXPERIENCE
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-300 ${isExperienceOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {isExperienceOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 bg-white rounded-xl shadow-lg border border-reset-border py-3 min-w-[200px]">
                    {experienceLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsExperienceOpen(false)}
                        className={`block px-6 py-3 text-xs font-semibold tracking-widest transition-colors duration-300 cursor-pointer ${
                          location.pathname === link.path
                            ? 'text-reset-sage bg-reset-sage-light'
                            : 'text-reset-dark hover:text-reset-sage hover:bg-reset-bg-alt'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {rightNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-xs font-semibold tracking-widest transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                    location.pathname === link.path
                      ? 'text-reset-sage'
                      : 'text-reset-dark hover:text-reset-sage'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Cart Icon */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative text-reset-dark hover:text-reset-sage transition-colors cursor-pointer"
                aria-label="Open cart"
              >
                <i className="ri-shopping-bag-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-reset-sage text-white text-xs font-semibold w-5 h-5 rounded-full flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>

              <Link
                to="/contact"
                className="bg-reset-sage text-white hover:bg-reset-sage-hover px-6 py-3 rounded-full text-xs font-semibold tracking-widest transition-all duration-300 whitespace-nowrap cursor-pointer uppercase shadow-sm hover:shadow-md"
              >
                BOOK
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="lg:hidden flex items-center gap-4">
              {/* Mobile Cart Icon */}
              <button
                onClick={() => setIsCartOpen(true)}
                className="relative text-reset-dark hover:text-reset-sage transition-colors cursor-pointer"
                aria-label="Open cart"
              >
                <i className="ri-shopping-bag-3-line text-xl w-6 h-6 flex items-center justify-center"></i>
                {totalItems > 0 && (
                  <span className="absolute -top-2 -right-2 bg-reset-sage text-white text-xs font-semibold w-5 h-5 rounded-full flex items-center justify-center">
                    {totalItems}
                  </span>
                )}
              </button>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="w-10 h-10 flex items-center justify-center text-reset-dark hover:text-reset-sage transition-colors duration-300 cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl w-6 h-6 flex items-center justify-center`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden mt-6 pb-6 border-t border-reset-border pt-6">
              <div className="flex flex-col gap-6">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-xs font-semibold tracking-widest transition-colors duration-300 cursor-pointer ${
                      location.pathname === link.path
                        ? 'text-reset-sage'
                        : 'text-reset-dark hover:text-reset-sage'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}

                {/* Mobile Experience Section */}
                <div className="space-y-4">
                  <p className="text-xs font-semibold tracking-widest text-reset-muted">
                    EXPERIENCE
                  </p>
                  <div className="pl-4 space-y-4 border-l-2 border-reset-sage-light">
                    {experienceLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block text-xs font-semibold tracking-widest transition-colors duration-300 cursor-pointer ${
                          location.pathname === link.path
                            ? 'text-reset-sage'
                            : 'text-reset-dark hover:text-reset-sage'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {rightNavLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-xs font-semibold tracking-widest transition-colors duration-300 cursor-pointer ${
                      location.pathname === link.path
                        ? 'text-reset-sage'
                        : 'text-reset-dark hover:text-reset-sage'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <Link
                  to="/contact"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="bg-reset-sage text-white hover:bg-reset-sage-hover px-6 py-3 rounded-full text-xs font-semibold tracking-widest transition-all duration-300 text-center cursor-pointer uppercase shadow-sm hover:shadow-md"
                >
                  BOOK
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Cart Sidebar */}
      <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </>
  );
}